import { Button } from '@/app/common/components/button/Button'
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '@/common/components/Card'
import Pagination from '@/common/components/Pagination'
import SearchInput from '@/common/components/SearchInput'
import { CircleArrowsIcon } from '@/common/icons/arrows'
import { cn } from '@/common/utilities'
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'

interface DataTableProps<TData> {
  // i have been trying to properly type the columns prop.
  // I can't make it work and it seems like a pain.
  // For now, let's leave this as any, if you know how to properly type it please go ahead.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: any
  title?: string
  totalRows: number
  currentPage: number
  recordsPerPage: number
  search: string
  onSearchChange: (value: string) => void
  onPageChange: (page: number) => void
  onPerPageChange: (perPage: number) => void
  onRefresh: () => void
  lastUpdated?: string
  searchPlaceholder?: string
  errorMessage?: string
  loadingMessage?: string
  emptyMessage?: string
  data: TData[]
  status?: 'pending' | 'error' | 'success'
  filter: React.ReactNode
}

export function ACETable<TData>({
  columns,
  title = 'Data Table',
  totalRows,
  currentPage,
  recordsPerPage,
  search,
  onSearchChange,
  onPageChange,
  onPerPageChange,
  onRefresh,
  lastUpdated = '',
  searchPlaceholder = 'Search',
  errorMessage = 'Error fetching data.',
  loadingMessage = 'Loading...',
  emptyMessage = 'No data available.',
  data = [],
  status,
  filter,
}: DataTableProps<TData>) {
  const table = useReactTable({
    data: data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: Math.ceil(totalRows / recordsPerPage) || 1,
  })

  const isEmpty = !data || data.length === 0

  const renderTableContent = () => {
    if (status === 'pending') {
      return (
        <CardContent className="flex items-center justify-center py-12 text-gray-500">
          {loadingMessage}
        </CardContent>
      )
    }

    if (status === 'error') {
      return (
        <CardContent className="flex items-center justify-center py-12 text-red-500">
          {errorMessage}
        </CardContent>
      )
    }

    if (isEmpty) {
      return (
        <CardContent className="flex items-center h-full justify-center py-12 text-gray-600 ">
          {emptyMessage}
        </CardContent>
      )
    }

    return (
      <CardContent className="flex flex-col  p-0 lg:p-0 h-full w-full">
        <table>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header, index) => (
                  <th
                    key={header.id}
                    className={cn(
                      'px-2 py-3.5 text-left text-sm font-semibold text-gray-900 md:py-4 md:px-2.5',
                      index != 0 &&
                        index != headerGroup.headers.length - 1 &&
                        'hidden md:table-cell',
                      index == headerGroup.headers.length - 1 && 'text-left'
                    )}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell, index) => (
                  <td
                    key={cell.id}
                    className={cn(
                      'py-4 px-2 text-sm font-sans text-left text-gray-700 border-t border-gray-200 md:py-4 md:px-2',
                      index != 0 &&
                        index != row.getVisibleCells().length - 1 &&
                        'hidden md:table-cell',
                      index == row.getVisibleCells().length - 1 && 'text-left'
                    )}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    )
  }

  return (
    <section aria-labelledby="data-table-title">
      <Card className="w-full shadow-sm">
        <CardHeader className="flex justify-between items-center text-md font-semibold">
          <div className="text-left text-lg md:text-md text-gray-900">
            {title}
            {lastUpdated && (
              <span className="hidden mt-1 text-sm font-normal text-gray-400 lg-sm:block lg-sm:w-full">
                {`last updated at: ${lastUpdated}`}
              </span>
            )}
          </div>
          <div>
            <div className="space-y-3 flex flex-col items-start lg-sm:flex-row lg-sm:items-center lg-sm:space-y-0 lg-sm:space-x-4">
              {filter}
              <SearchInput
                id="table_search"
                placeholder={searchPlaceholder}
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
              />
              <div className="flex space-x-2 lg-sm:flex-col lg-sm:space-x-0">
                <Button
                  variant="outline"
                  className="max-w-12"
                  size="md"
                  disabled={status === 'pending'}
                  onClick={onRefresh}
                >
                  <CircleArrowsIcon className="text-base" />
                </Button>
                {lastUpdated && (
                  <span className="block w-15ch text-sm font-normal text-gray-400 lg-sm:w-full lg-sm:hidden">
                    {`last updated at: ${lastUpdated}`}
                  </span>
                )}
              </div>
            </div>
          </div>
        </CardHeader>

        {renderTableContent()}

        <CardFooter className="p-0 border-t border-transparent mt-5">
          <Pagination
            itemsPerPage={recordsPerPage}
            currentPage={currentPage}
            totalRows={totalRows}
            onPageChange={onPageChange}
            onPerPageChange={onPerPageChange}
          />
        </CardFooter>
      </Card>
    </section>
  )
}
